/**
 * Cache Configuration - LLM cache status and config for a bot
 * Route: /bots/:botId/cache
 * Guard: user must have access to bot via tenantBots
 */
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router';
import { useBotRoute } from '../lib/useBotRoute';
import { botPath } from '../lib/botNav';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import {
  hubService,
  type LlmCacheStatus,
  type LlmCacheStats,
  type SemanticCacheEntry,
  type RedisCacheEntry,
} from '../services/hub.service';


export function CacheConfig() {
  const { system, botId } = useBotRoute();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { tenants, tenantBots } = useTenant();
  const [status, setStatus] = useState<LlmCacheStatus | null>(null);
  const [stats, setStats] = useState<LlmCacheStats | null>(null);
  const [semanticItems, setSemanticItems] = useState<SemanticCacheEntry[]>([]);
  const [responseItems, setResponseItems] = useState<RedisCacheEntry[]>([]);
  const [retrievalItems, setRetrievalItems] = useState<RedisCacheEntry[]>([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ query: '', response: '' });

  const tenant = tenants?.find((t) => {
    const bots = tenantBots?.[t.id] ?? [];
    return bots.some((b) => b.botId === botId);
  });
  const bots = tenant ? tenantBots?.[tenant.id] ?? [] : [];
  const hasAccess = bots.some((b) => b.botId === botId);
  const bot = bots.find((b) => b.botId === botId);

  useEffect(() => {
    if (!tenant || !hasAccess) {
      navigate('/');
    }
  }, [tenant, hasAccess, navigate]);

  const load = useCallback(async () => {
    if (!bot?.id) return;
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const [statusData, statsData, semanticData, responseData, retrievalData] = await Promise.all([
        hubService.getCacheStatus(bot.id, system, token),
        hubService.getCacheStats(bot.id, system, token),
        hubService.listSemanticCache(bot.id, system, 50, undefined, token),
        hubService.listResponseCache(bot.id, system, 50, token),
        hubService.listRetrievalCache(bot.id, system, 50, token),
      ]);
      setStatus(statusData);
      setStats(statsData);
      setSemanticItems(semanticData.items);
      setResponseItems(responseData);
      setRetrievalItems(retrievalData);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [bot?.id, getToken]);

  const loadRedisLists = useCallback(async () => {
    if (!bot?.id) return;
    setLoadingLists(true);
    try {
      const token = getToken();
      const [responseData, retrievalData] = await Promise.all([
        hubService.listResponseCache(bot.id, system, 50, token),
        hubService.listRetrievalCache(bot.id, system, 50, token),
      ]);
      setResponseItems(responseData);
      setRetrievalItems(retrievalData);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Redis entries');
    } finally {
      setLoadingLists(false);
    }
  }, [bot?.id, getToken]);

  useEffect(() => {
    if (bot?.id && hasAccess) {
      load();
    }
  }, [bot?.id, hasAccess, load]);

  async function handleToggleLayer(layer: 'semantic' | 'response' | 'retrieval') {
    if (!bot?.id || !status) return;
    const current = {
      semantic: status.semanticCache?.enabled ?? false,
      response: status.responseCache?.enabled ?? false,
      retrieval: status.retrievalCache?.enabled ?? false,
    };
    const next = { ...current, [layer]: !current[layer] };
    setSaving(true);
    setError(null);
    try {
      const token = getToken();
      await hubService.updateCacheConfig(bot.id, next, system, token);
      setStatus((s) =>
        s
          ? {
              ...s,
              enabled: next.semantic || next.response || next.retrieval,
              semanticCache: { enabled: next.semantic },
              responseCache: { enabled: next.response },
              retrievalCache: { enabled: next.retrieval },
            }
          : null,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleClearCache() {
    if (!bot?.id || !confirm('Clear all cached data for this bot? This cannot be undone.')) return;
    setClearing(true);
    setError(null);
    try {
      const token = getToken();
      const result = await hubService.clearBotCache(bot.id, system, token);
      setStats({ semantic: 0, response: 0, retrieval: 0 });
      setSemanticItems([]);
      setResponseItems([]);
      setRetrievalItems([]);
      setError(null);
      alert(`Cleared: ${result.semantic} semantic, ${result.response} response, ${result.retrieval} retrieval entries.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Clear failed');
    } finally {
      setClearing(false);
    }
  }

  async function handleCreateSemantic() {
    if (!bot?.id || !form.query.trim() || !form.response.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const token = getToken();
      const created = await hubService.createSemanticCache(bot.id, form.query, form.response, system, token);
      setSemanticItems((prev) => [created, ...prev]);
      setStats((s) => (s ? { ...s, semantic: s.semantic + 1 } : null));
      setCreating(false);
      setForm({ query: '', response: '' });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateSemantic(id: string, updates: { query?: string; response?: string }) {
    if (!bot?.id) return;
    setSaving(true);
    setError(null);
    try {
      const token = getToken();
      const updated = await hubService.updateSemanticCache(bot.id, id, updates, system, token);
      setSemanticItems((prev) => prev.map((i) => (i.id === id ? updated : i)));
      setEditingId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteSemantic(id: string) {
    if (!bot?.id || !confirm('Delete this semantic cache entry?')) return;
    setSaving(true);
    setError(null);
    try {
      const token = getToken();
      await hubService.deleteSemanticCache(bot.id, id, system, token);
      setSemanticItems((prev) => prev.filter((i) => i.id !== id));
      setStats((s) => (s ? { ...s, semantic: Math.max(0, s.semantic - 1) } : null));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setSaving(false);
    }
  }

  async function handlePromoteToFaq(id: string) {
    if (!bot?.id || !confirm('Promote this entry to FAQ? It will be added as a new FAQ entry.')) return;
    setSaving(true);
    setError(null);
    try {
      const token = getToken();
      await hubService.promoteSemanticToFaq(bot.id, id, system, { language: 'mn' }, token);
      alert('Promoted to FAQ successfully.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Promote failed');
    } finally {
      setSaving(false);
    }
  }

  if (!tenant || !hasAccess || !bot?.id) {
    return null;
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="flex items-center gap-4 mb-6">
          <Link to={botPath({ system, botId }, 'config')} className="text-gray-500 hover:text-gray-700 text-sm">
            Config
          </Link>
          <Link to={botPath({ system, botId }, 'usage')} className="text-gray-500 hover:text-gray-700 text-sm">
            Usage
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {bot?.botName || botId} — Cache
          </h1>
        </div>
        <p className="text-gray-600 mb-6">
          LLM cache status and configuration for semantic, response, and retrieval caching.
        </p>

        {loading && <div className="text-gray-500">Loading...</div>}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
        )}
        {!loading && !error && status && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h2 className="text-sm font-semibold text-gray-900">Per-layer cache</h2>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="p-3 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-between">
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                        Semantic (Qdrant)
                      </div>
                      <p className="text-xs text-gray-500">Similar queries</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={status.semanticCache?.enabled}
                      onClick={() => handleToggleLayer('semantic')}
                      disabled={saving}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${
                        status.semanticCache?.enabled ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                          status.semanticCache?.enabled ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <div className="p-3 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-between">
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                        Response (Redis)
                      </div>
                      <p className="text-xs text-gray-500">Exact match</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={status.responseCache?.enabled}
                      onClick={() => handleToggleLayer('response')}
                      disabled={saving}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${
                        status.responseCache?.enabled ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                          status.responseCache?.enabled ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <div className="p-3 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-between">
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase mb-1">
                        Retrieval (Redis)
                      </div>
                      <p className="text-xs text-gray-500">RAG results</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={status.retrievalCache?.enabled}
                      onClick={() => handleToggleLayer('retrieval')}
                      disabled={saving}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${
                        status.retrievalCache?.enabled ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                          status.retrievalCache?.enabled ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {stats != null && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-900">Cached Data</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => load()}
                      disabled={loading}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50"
                    >
                      Refresh
                    </button>
                    <button
                      onClick={handleClearCache}
                      disabled={
                        clearing ||
                        (stats.semantic === 0 && stats.response === 0 && stats.retrieval === 0)
                      }
                      className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {clearing ? 'Clearing...' : 'Clear All'}
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase">
                        Semantic (Qdrant)
                      </dt>
                      <dd className="mt-1 text-lg font-mono">{stats.semantic}</dd>
                      <p className="text-xs text-gray-500">entries</p>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase">
                        Response (Redis)
                      </dt>
                      <dd className="mt-1 text-lg font-mono">{stats.response}</dd>
                      <p className="text-xs text-gray-500">entries</p>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase">
                        Retrieval (Redis)
                      </dt>
                      <dd className="mt-1 text-lg font-mono">{stats.retrieval}</dd>
                      <p className="text-xs text-gray-500">entries</p>
                    </div>
                  </dl>

                  {(stats.response > 0 || stats.retrieval > 0) && (
                    <p className="text-xs text-gray-500 mb-2">
                      Response and retrieval entries are listed below. Scroll down in this card to see each key and value.
                    </p>
                  )}

                  {stats.response > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">
                        Response cache (Redis) — {responseItems.length} of {stats.response} shown
                      </h3>
                      {responseItems.length === 0 ? (
                        <div className="text-xs text-amber-700 space-y-1">
                          <p>No entries loaded. The list API may not be deployed yet, or keys may have expired.</p>
                          <button
                            type="button"
                            onClick={loadRedisLists}
                            disabled={loadingLists}
                            className="mt-2 px-2 py-1 rounded bg-amber-100 hover:bg-amber-200 text-amber-800 disabled:opacity-50"
                          >
                            {loadingLists ? 'Loading…' : 'Load response/retrieval entries'}
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {responseItems.map((entry) => (
                            <div key={entry.key} className="p-2 rounded border border-gray-200 bg-gray-50 text-xs">
                              <div className="font-mono text-gray-600 truncate" title={entry.key}>
                                {entry.key}
                              </div>
                              <div className="mt-1 text-gray-800 whitespace-pre-wrap break-words">
                                {typeof entry.value === 'string'
                                  ? entry.value.length > 300
                                    ? `${entry.value.slice(0, 300)}...`
                                    : entry.value
                                  : JSON.stringify(entry.value).slice(0, 300)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {stats.retrieval > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h3 className="text-sm font-medium text-gray-700 mb-2">
                        Retrieval cache (Redis) — {retrievalItems.length} of {stats.retrieval} shown
                      </h3>
                      {retrievalItems.length === 0 ? (
                        <div className="text-xs text-amber-700">
                          <p>No entries loaded. Use the button above (under Response) to load key/value list.</p>
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {retrievalItems.map((entry) => (
                            <div key={entry.key} className="p-2 rounded border border-gray-200 bg-gray-50 text-xs">
                              <div className="font-mono text-gray-600 truncate" title={entry.key}>
                                {entry.key}
                              </div>
                              <div className="mt-1 text-gray-800">
                                {typeof entry.value === 'string'
                                  ? entry.value.length > 300
                                    ? `${entry.value.slice(0, 300)}...`
                                    : entry.value
                                  : Array.isArray(entry.value)
                                    ? `${entry.value.length} RAG result(s)`
                                    : JSON.stringify(entry.value).slice(0, 300)}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Semantic cache entries */}
            {status.semanticCache?.enabled && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-900">Semantic cache entries</h2>
                  {!creating ? (
                    <button
                      onClick={() => setCreating(true)}
                      className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md"
                    >
                      Add entry
                    </button>
                  ) : null}
                </div>
                <div className="p-4">
                  {creating && (
                    <div className="mb-4 p-4 rounded-lg border border-gray-200 bg-gray-50 space-y-3">
                      <input
                        placeholder="Query"
                        value={form.query}
                        onChange={(e) => setForm((f) => ({ ...f, query: e.target.value }))}
                        className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                      />
                      <textarea
                        placeholder="Response"
                        value={form.response}
                        onChange={(e) => setForm((f) => ({ ...f, response: e.target.value }))}
                        className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                        rows={3}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleCreateSemantic}
                          disabled={saving || !form.query.trim() || !form.response.trim()}
                          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setCreating(false);
                            setForm({ query: '', response: '' });
                          }}
                          className="px-3 py-1.5 text-sm bg-gray-200 rounded"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="space-y-2">
                    {semanticItems.map((item) => (
                      <div
                        key={item.id}
                        className="p-3 rounded-lg border border-gray-200 bg-white"
                      >
                        {editingId === item.id ? (
                          <div className="space-y-2">
                            <input
                              defaultValue={item.query}
                              id={`edit-q-${item.id}`}
                              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                            />
                            <textarea
                              defaultValue={item.response}
                              id={`edit-r-${item.id}`}
                              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              rows={2}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  const q = (document.getElementById(`edit-q-${item.id}`) as HTMLInputElement)?.value;
                                  const r = (document.getElementById(`edit-r-${item.id}`) as HTMLTextAreaElement)?.value;
                                  if (q && r) handleUpdateSemantic(item.id, { query: q, response: r });
                                }}
                                disabled={saving}
                                className="px-2 py-1 text-sm bg-blue-600 text-white rounded"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingId(null)}
                                className="px-2 py-1 text-sm bg-gray-200 rounded"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-gray-900">{item.query}</p>
                            <p className="text-sm text-gray-600 mt-1">
                              {item.response.length > 200 ? `${item.response.slice(0, 200)}...` : item.response}
                            </p>
                            <div className="mt-2 flex gap-2">
                              <button
                                onClick={() => setEditingId(item.id)}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handlePromoteToFaq(item.id)}
                                disabled={saving}
                                className="text-xs text-green-600 hover:underline"
                              >
                                Promote to FAQ
                              </button>
                              <button
                                onClick={() => handleDeleteSemantic(item.id)}
                                disabled={saving}
                                className="text-xs text-red-600 hover:underline"
                              >
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                    {semanticItems.length === 0 && !creating && (
                      <p className="text-sm text-gray-500">No semantic cache entries.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {status.config && (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <h2 className="text-sm font-semibold text-gray-900">Configuration</h2>
                </div>
                <div className="p-4">
                  <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {status.config.responseTtlSec != null && (
                      <div>
                        <dt className="text-xs font-medium text-gray-500 uppercase">
                          Response TTL (sec)
                        </dt>
                        <dd className="mt-1 text-sm font-mono">{status.config.responseTtlSec}</dd>
                      </div>
                    )}
                    {status.config.retrievalTtlSec != null && (
                      <div>
                        <dt className="text-xs font-medium text-gray-500 uppercase">
                          Retrieval TTL (sec)
                        </dt>
                        <dd className="mt-1 text-sm font-mono">{status.config.retrievalTtlSec}</dd>
                      </div>
                    )}
                    {status.config.semanticThreshold != null && (
                      <div>
                        <dt className="text-xs font-medium text-gray-500 uppercase">
                          Semantic Threshold
                        </dt>
                        <dd className="mt-1 text-sm font-mono">{status.config.semanticThreshold}</dd>
                      </div>
                    )}
                  </dl>
                  <p className="mt-2 text-xs text-gray-500">
                    Configure via env: LLM_CACHE_RESPONSE_TTL_SEC, LLM_CACHE_RETRIEVAL_TTL_SEC,
                    LLM_CACHE_SEMANTIC_THRESHOLD
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
